import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
// web/src/pages/OnboardingPage.tsx — post-signup onboarding (v2.7).
//
// Three pages, driven entirely by data/reference/interactive_cards/
// profile_tour_step.json:
//   1. You          — age, country, state, filing, spouse, kids, employment, goal
//   2. Money         — income by kind (4) + monthly expenses
//   3. Net worth     — asset buckets (4) + debt buckets (4)
//
// Modern input choices:
//   - currency  → $-prefixed, comma-formatted, numeric-only
//   - us_state  → searchable native select with 50-state list
//   - segmented → single_select pills (multi-row, tap-friendly)
//   - comma_numbers → free text for "14, 17" style dependent-ages list
//   - conditional reveal via step.depends_on
// Group totals (net worth = assets − debts) update live at the bottom of
// the net_worth page so the user sees their position as they type.
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AppShell } from "../components/layout/AppShell";
import { api, ApiError, formatApiError, } from "../lib/apiClient";
// ── US states for the state dropdown ─────────────────────────────────────────
const US_STATES = [
    { code: "AL", name: "Alabama" }, { code: "AK", name: "Alaska" },
    { code: "AZ", name: "Arizona" }, { code: "AR", name: "Arkansas" },
    { code: "CA", name: "California" }, { code: "CO", name: "Colorado" },
    { code: "CT", name: "Connecticut" }, { code: "DE", name: "Delaware" },
    { code: "DC", name: "District of Columbia" },
    { code: "FL", name: "Florida" }, { code: "GA", name: "Georgia" },
    { code: "HI", name: "Hawaii" }, { code: "ID", name: "Idaho" },
    { code: "IL", name: "Illinois" }, { code: "IN", name: "Indiana" },
    { code: "IA", name: "Iowa" }, { code: "KS", name: "Kansas" },
    { code: "KY", name: "Kentucky" }, { code: "LA", name: "Louisiana" },
    { code: "ME", name: "Maine" }, { code: "MD", name: "Maryland" },
    { code: "MA", name: "Massachusetts" }, { code: "MI", name: "Michigan" },
    { code: "MN", name: "Minnesota" }, { code: "MS", name: "Mississippi" },
    { code: "MO", name: "Missouri" }, { code: "MT", name: "Montana" },
    { code: "NE", name: "Nebraska" }, { code: "NV", name: "Nevada" },
    { code: "NH", name: "New Hampshire" }, { code: "NJ", name: "New Jersey" },
    { code: "NM", name: "New Mexico" }, { code: "NY", name: "New York" },
    { code: "NC", name: "North Carolina" }, { code: "ND", name: "North Dakota" },
    { code: "OH", name: "Ohio" }, { code: "OK", name: "Oklahoma" },
    { code: "OR", name: "Oregon" }, { code: "PA", name: "Pennsylvania" },
    { code: "RI", name: "Rhode Island" }, { code: "SC", name: "South Carolina" },
    { code: "SD", name: "South Dakota" }, { code: "TN", name: "Tennessee" },
    { code: "TX", name: "Texas" }, { code: "UT", name: "Utah" },
    { code: "VT", name: "Vermont" }, { code: "VA", name: "Virginia" },
    { code: "WA", name: "Washington" }, { code: "WV", name: "West Virginia" },
    { code: "WI", name: "Wisconsin" }, { code: "WY", name: "Wyoming" },
];
// ── Helpers ──────────────────────────────────────────────────────────────────
function fmtCurrency(n) {
    if (n == null || Number.isNaN(n))
        return "$0";
    return "$" + Math.round(n).toLocaleString("en-US");
}
function shouldShow(step, values) {
    const d = step.depends_on;
    if (!d)
        return true;
    const v = values[d.key];
    if (d.equals !== undefined)
        return v === d.equals;
    if (d.in !== undefined && Array.isArray(d.in))
        return d.in.includes(v);
    if (d.gt !== undefined) {
        const n = typeof v === "number" ? v : Number(v);
        return !Number.isNaN(n) && n > d.gt;
    }
    return true;
}
// ── Page ─────────────────────────────────────────────────────────────────────
export default function OnboardingPage() {
    const navigate = useNavigate();
    const [groups, setGroups] = useState(null);
    const [page, setPage] = useState(0);
    const [values, setValues] = useState({});
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    useEffect(() => {
        api.getOnboardingSteps()
            .then((res) => setGroups(res.groups))
            .catch((err) => {
            setError(err instanceof ApiError ? formatApiError(err) : String(err));
        });
    }, []);
    const currentGroup = groups?.[page] ?? null;
    const isLast = useMemo(() => (groups ? page >= groups.length - 1 : false), [groups, page]);
    function setField(key, value) {
        setValues((prev) => ({ ...prev, [key]: value }));
    }
    async function finish() {
        setSaving(true);
        setError(null);
        try {
            const answers = {};
            for (const [k, v] of Object.entries(values)) {
                if (v === undefined || v === null || v === "")
                    continue;
                answers[k] = v;
            }
            await api.completeOnboarding({ answers });
            navigate("/", { replace: true });
        }
        catch (err) {
            setError(err instanceof ApiError ? formatApiError(err) : String(err));
        }
        finally {
            setSaving(false);
        }
    }
    // Live group totals (only meaningful on the net_worth page).
    const totals = useMemo(() => {
        let assets = 0, debts = 0;
        for (const g of groups ?? []) {
            for (const s of g.steps) {
                if (s.bucket_class === "asset")
                    assets += Number(values[s.key] || 0);
                if (s.bucket_class === "liability")
                    debts += Number(values[s.key] || 0);
            }
        }
        return { assets, debts, net: assets - debts };
    }, [values, groups]);
    if (!groups) {
        return (_jsx(AppShell, { children: _jsx("div", { className: "h-full flex items-center justify-center text-slate-400 text-sm", children: error ? _jsx("span", { className: "text-rose-400", children: error }) : "Loading…" }) }));
    }
    return (_jsx(AppShell, { children: _jsx("div", { className: "h-full overflow-y-auto bg-slate-950 text-slate-200 flex items-start justify-center px-4 py-10", children: _jsxs("div", { className: "w-full max-w-2xl space-y-6", children: [_jsx("div", { className: "flex gap-2", children: groups.map((_, i) => (_jsx("div", { className: `h-1 flex-1 rounded-full transition-colors ${i <= page ? "bg-violet-500" : "bg-slate-800"}` }, i))) }), _jsxs("div", { children: [_jsx("h1", { className: "text-2xl font-semibold text-white", children: currentGroup?.title ?? "" }), _jsx("p", { className: "mt-1 text-sm text-slate-500", children: currentGroup?.subtitle ?? "" })] }), error && (_jsx("div", { className: "text-xs text-rose-400 bg-rose-500/10 border border-rose-500/20 rounded-lg px-3 py-2", children: error })), _jsx("div", { className: "space-y-4", children: (currentGroup?.steps ?? []).map((step) => shouldShow(step, values) ? (_jsx(FieldRow, { step: step, value: values[step.key], onChange: (v) => setField(step.key, v) }, step.key)) : null) }), currentGroup?.key === "net_worth" && (totals.assets > 0 || totals.debts > 0) && (_jsxs("div", { className: "rounded-xl border border-slate-800 bg-slate-900/50 px-4 py-3 grid grid-cols-3 gap-3", children: [_jsx(Stat, { label: "Assets", value: fmtCurrency(totals.assets), tone: "up" }), _jsx(Stat, { label: "Debts", value: fmtCurrency(totals.debts), tone: "down" }), _jsx(Stat, { label: "Net worth", value: fmtCurrency(totals.net), tone: totals.net >= 0 ? "up" : "down" })] })), _jsxs("div", { className: "flex items-center justify-between pt-2", children: [_jsx("button", { onClick: finish, disabled: saving, className: "text-xs text-slate-500 hover:text-slate-300 transition-colors disabled:opacity-50", children: "Skip all \u2014 I'll fill this in later" }), _jsxs("div", { className: "flex gap-2", children: [page > 0 && (_jsx("button", { onClick: () => setPage((p) => p - 1), disabled: saving, className: "rounded-lg px-4 py-2 text-sm text-slate-400 hover:text-slate-200 transition-colors disabled:opacity-50", children: "Back" })), !isLast ? (_jsx("button", { onClick: () => setPage((p) => p + 1), disabled: saving, className: "rounded-lg bg-violet-600 hover:bg-violet-500 px-5 py-2 text-sm font-medium text-white disabled:opacity-50 transition-colors", children: "Next" })) : (_jsx("button", { onClick: finish, disabled: saving, className: "rounded-lg bg-violet-600 hover:bg-violet-500 px-5 py-2 text-sm font-medium text-white disabled:opacity-50 transition-colors", children: saving ? "Saving…" : "Finish" }))] })] })] }) }) }));
}
function Stat({ label, value, tone }) {
    const cls = tone === "up" ? "text-emerald-400" :
        tone === "down" ? "text-rose-400" : "text-slate-100";
    return (_jsxs("div", { children: [_jsx("div", { className: "text-[10px] font-semibold uppercase tracking-wider text-slate-500", children: label }), _jsx("div", { className: `text-base font-semibold mt-0.5 ${cls}`, children: value })] }));
}
// ── FieldRow — dispatches on input_type ──────────────────────────────────────
function FieldRow({ step, value, onChange, }) {
    const label = step.phrasing?.default ?? step.key;
    const hint = step.hint ?? undefined;
    if (step.input_type === "single_select") {
        return (_jsxs("div", { className: "space-y-2", children: [_jsx("label", { className: "text-sm text-slate-300", children: label }), _jsx("div", { className: "flex flex-wrap gap-2", children: (step.options ?? []).map((o) => {
                        const selected = value === o.value;
                        return (_jsx("button", { type: "button", onClick: () => onChange(o.value), className: `rounded-lg border px-3 py-1.5 text-xs transition-colors ${selected
                                ? "border-violet-500 bg-violet-600/20 text-violet-200"
                                : "border-slate-800 bg-slate-900 text-slate-400 hover:border-slate-700"}`, children: o.label }, o.value));
                    }) }), hint && _jsx("p", { className: "text-xs text-slate-600", children: hint })] }));
    }
    if (step.input_type === "us_state") {
        return (_jsxs("div", { className: "space-y-1", children: [_jsx("label", { className: "text-sm text-slate-300", children: label }), _jsxs("select", { value: value ?? "", onChange: (e) => onChange(e.target.value || undefined), className: "w-full bg-slate-900 border border-slate-800 rounded-lg px-3 py-2 text-sm text-slate-200 focus:outline-none focus:border-violet-500/60 transition-colors", children: [_jsx("option", { value: "", children: "Select state\u2026" }), US_STATES.map((s) => (_jsx("option", { value: s.code, children: s.name }, s.code)))] }), hint && _jsx("p", { className: "text-xs text-slate-600", children: hint })] }));
    }
    if (step.input_type === "currency") {
        const display = value === undefined || value === null || value === ""
            ? ""
            : Number(value).toLocaleString("en-US");
        return (_jsxs("div", { className: "space-y-1", children: [_jsx("label", { className: "text-sm text-slate-300", children: label }), _jsxs("div", { className: "relative", children: [_jsx("span", { className: "absolute left-3 top-1/2 -translate-y-1/2 text-sm text-slate-500 pointer-events-none", children: "$" }), _jsx("input", { type: "text", inputMode: "numeric", placeholder: step.placeholder?.replace(/^\$/, "") ?? "0", value: display, onChange: (e) => {
                                const raw = e.target.value.replace(/[^0-9.]/g, "");
                                if (raw === "")
                                    onChange(undefined);
                                else
                                    onChange(Number(raw));
                            }, className: "w-full bg-slate-900 border border-slate-800 rounded-lg pl-7 pr-3 py-2 text-sm text-slate-200 placeholder-slate-600 focus:outline-none focus:border-violet-500/60 transition-colors tabular-nums" })] }), hint && _jsx("p", { className: "text-xs text-slate-600", children: hint })] }));
    }
    if (step.input_type === "comma_numbers") {
        return (_jsxs("div", { className: "space-y-1", children: [_jsx("label", { className: "text-sm text-slate-300", children: label }), _jsx("input", { type: "text", inputMode: "numeric", placeholder: step.placeholder ?? "", value: value ?? "", onChange: (e) => onChange(e.target.value || undefined), className: "w-full bg-slate-900 border border-slate-800 rounded-lg px-3 py-2 text-sm text-slate-200 placeholder-slate-600 focus:outline-none focus:border-violet-500/60 transition-colors tabular-nums" }), hint && _jsx("p", { className: "text-xs text-slate-600", children: hint })] }));
    }
    // text / number / date fallback
    return (_jsxs("div", { className: "space-y-1", children: [_jsx("label", { className: "text-sm text-slate-300", children: label }), _jsx("input", { type: step.input_type === "number" ? "number" : step.input_type === "date" ? "date" : "text", placeholder: step.placeholder ?? "", value: value ?? "", onChange: (e) => {
                    const raw = e.target.value;
                    if (step.input_type === "number") {
                        onChange(raw === "" ? undefined : Number(raw));
                    }
                    else {
                        onChange(raw);
                    }
                }, className: "w-full bg-slate-900 border border-slate-800 rounded-lg px-3 py-2 text-sm text-slate-200 placeholder-slate-600 focus:outline-none focus:border-violet-500/60 transition-colors" }), hint && _jsx("p", { className: "text-xs text-slate-600", children: hint })] }));
}
